import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import {
  FormGroup,
  Input,
  Alert,
  FormText,
} from 'reactstrap';
import { Button, CardTitle } from '@cordata/buffet';

import {
  referenceAdminInstance as refAdmin,
} from '~/utils';

const PREDICATE_HELP = `JSON Logic predicate array. Standard pattern:
[{"some": [{"var": "changes"}, {"and": [
    {"==": [{"var": "section"}, "person"]},
    {"field_set": "death_date"}
]}]}]

Available custom operators: field_set, field_changed, field_cleared
Empty array [] = catch-all (matches all submissions)

Note: Validation only checks structural correctness (known operators, arity).
It does not verify that field names, section names, or string values are valid.`;

const PredicateField = () => {
  const { values, setFieldValue } = useFormikContext();
  const [predicateErrors, setPredicateErrors] = useState(null);
  const [predicateValid, setPredicateValid] = useState(null);
  const [validating, setValidating] = useState(false);

  const validatePredicates = () => {
    setValidating(true);
    setPredicateErrors(null);
    setPredicateValid(null);

    let parsed;
    try {
      parsed = JSON.parse(values.predicates);
    } catch (e) {
      setPredicateErrors([`Invalid JSON: ${e.message}`]);
      setValidating(false);
      return;
    }

    refAdmin
      .post('/reference-topic/validate-predicate', { predicates: JSON.stringify(parsed) })
      .then((r) => {
        const result = r.data;
        if (result.valid) {
          setPredicateValid(true);
          setPredicateErrors(null);
        } else {
          setPredicateValid(false);
          setPredicateErrors(result.errors);
        }
      })
      .catch(() => {
        setPredicateErrors(['Failed to reach validation endpoint']);
      })
      .finally(() => setValidating(false));
  };

  return (
    <>
      <CardTitle>Predicates (JSON Logic)</CardTitle>

      <FormGroup>
        <Input
          type="textarea"
          name="predicates"
          id="predicates"
          rows={8}
          value={values.predicates || '[]'}
          onChange={e => setFieldValue('predicates', e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
        />
        <div className="mt-2 d-flex align-items-center">
          <Button
            type="button"
            color="info"
            size="sm"
            outline
            onClick={validatePredicates}
            disabled={validating}
          >
            {validating ? 'Validating...' : 'Validate Predicates'}
          </Button>
          {predicateValid && (
            <span className="text-success ml-2 small">Valid</span>
          )}
        </div>
        {predicateErrors && (
          <Alert color="danger" className="mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.85em' }}>
            <strong>Validation errors:</strong>
            <ul className="mb-0 pl-3 mt-1">
              {predicateErrors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </Alert>
        )}
        <FormText className="mt-2">
          <pre className="mb-0" style={{ fontSize: '0.75em', whiteSpace: 'pre-wrap' }}>
            {PREDICATE_HELP}
          </pre>
        </FormText>
      </FormGroup>
    </>
  );
};

export default PredicateField;
